import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import LoadingScreen from './components/ui/LoadingScreen';
import RouteSeo from './components/ui/RouteSeo';
import { hasAdminPermission } from './utils/adminPermissions';

const HomePage = lazy(() => import('./pages/HomePage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const UserDashboard = lazy(() => import('./pages/UserDashboard'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const SetupPage = lazy(() => import('./pages/SetupPage'));
const AdminRegisterPage = lazy(() => import('./pages/AdminRegisterPage'));
const SuperAdminDashboard = lazy(() => import('./pages/SuperAdminDashboard'));
const MembersPage = lazy(() => import('./pages/MembersPage'));
const ApplicationsPage = lazy(() => import('./pages/ApplicationsPage'));
const PublicDeathApplicationPage = lazy(() => import('./pages/PublicDeathApplicationPage'));
const GalleryPage = lazy(() => import('./pages/GalleryPage'));
const NiyamavaliPage = lazy(() => import('./pages/NiyamavaliPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

const getRoleDashboard = (user) => {
  if (user?.role === 'superadmin') return '/super-admin';
  if (user?.role === 'admin' || user?.role === 'subadmin') return '/admin';
  return '/dashboard';
};

const ProtectedRoute = ({ children, allowedRoles, requiredPermission }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) return <Navigate to={getRoleDashboard(user)} replace />;
  if (requiredPermission && !hasAdminPermission(user, requiredPermission)) {
    return <Navigate to={getRoleDashboard(user)} replace />;
  }
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (user) {
    if (user.role === 'superadmin') return <Navigate to="/super-admin" replace />;
    if (user.role === 'admin' || user.role === 'subadmin') return <Navigate to="/admin" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

export default function App() {
  const { loading } = useAuth();
  if (loading) return <LoadingScreen />;

  return (
    <Suspense fallback={<LoadingScreen />}>
      <RouteSeo />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/members" element={<MembersPage />} />
        <Route path="/applications" element={<ApplicationsPage />} />
        <Route path="/apply/death-assistance" element={<PublicDeathApplicationPage />} />
        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
        <Route path="/dashboard/*" element={
          <ProtectedRoute allowedRoles={['user','admin','superadmin']}>
            <UserDashboard />
          </ProtectedRoute>
        } />
        <Route path="/admin/*" element={
          <ProtectedRoute allowedRoles={['admin','subadmin','superadmin']}>
            <AdminDashboard />
          </ProtectedRoute>
        } />
        <Route path="/setup" element={<SetupPage />} />
        <Route path="/admin-register" element={<AdminRegisterPage />} />
        <Route path="/analytics" element={
          <ProtectedRoute allowedRoles={['admin','subadmin','superadmin']} requiredPermission="viewMember">
            <AnalyticsPage />
          </ProtectedRoute>
        } />
        <Route path="/super-admin/*" element={
          <ProtectedRoute allowedRoles={['superadmin']}>
            <SuperAdminDashboard />
          </ProtectedRoute>
        } />
        <Route path="/niyamavali" element={<NiyamavaliPage />} />
        <Route path="/gallery" element={<GalleryPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
